import { configAtom, siteCurrencyAtom } from "@/config.ts";
import type { DisplayAmount, SiteAmount, UntaggedAmount } from "@/modules/currency/currencyTypes.ts";
import { displayCurrencyAtom } from "@/modules/currency/displayCurrency.store.ts";
import { showError, softInvariant } from "@/modules/error/errorStream.ts";
import { getCurrencyFormat } from "@/modules/pricing/currencies.ts";
import { notNull } from "@warrenio/utils/notNull";
import { atom } from "jotai/vanilla";
import { selectAtom } from "jotai/vanilla/utils";
import { ceil, round } from "remeda";

export interface CurrencyInfo {
    code: string;
    title: string;
    /** `1` for site currency. */
    rate: number;

    decimal_digits: number;

    isSite: boolean;

    /** Convert an amount in this currency to the site currency */
    toSite(value: DisplayAmount | UntaggedAmount): SiteAmount;
    /** Convert an amount in the site currency to this currency */
    fromSite(value: SiteAmount | UntaggedAmount): DisplayAmount;

    /** `ceil` to appropriate decimal digits */
    ceil(value: DisplayAmount): DisplayAmount;
    /** currency-specific `round` to appropriate decimal digits */
    round(value: DisplayAmount): DisplayAmount;
}

const displayCurrenciesAtom = selectAtom(configAtom, (config) => config.displayCurrencies);

export const currenciesAtom = atom((get): CurrencyInfo[] => {
    let displayCurrencies = get(displayCurrenciesAtom) ?? [];
    const siteCurrency = get(siteCurrencyAtom);

    if (!displayCurrencies.find((c) => c.code === siteCurrency)) {
        // Fallback if site currency is not found (should always be found)
        const siteInfo = { code: siteCurrency, title: siteCurrency, rate: 1 };
        displayCurrencies = [...displayCurrencies, siteInfo];
    }

    return displayCurrencies.map(({ rate, code, title }): CurrencyInfo => {
        let format;
        try {
            format = getCurrencyFormat(code);
        } catch (e) {
            showError("Error getting currency format", e);
            format = { decimal_digits: 2 };
        }
        const { decimal_digits } = format;
        const isSite = code === siteCurrency;

        softInvariant(
            !isSite || rate === 1,
            `Invalid conversion rate: ${siteCurrency} → ${code} is ${rate}. The site currency's rate must be 1 or left unconfigured.`,
        );

        return {
            rate,
            code,
            title,

            decimal_digits,
            isSite,

            toSite: (value) => (value / rate) as SiteAmount,
            fromSite: (value) => (value * rate) as DisplayAmount,
            ceil: (value) => ceil(value, decimal_digits) as DisplayAmount,
            round: (value) => round(value, decimal_digits) as DisplayAmount,
        };
    });
});

export const siteCurrencyInfoAtom = atom((get): CurrencyInfo => notNull(get(currenciesAtom).find((f) => f.isSite)));

/** Info about the currently selected currency. */
export const currencyInfoAtom = atom((get): CurrencyInfo => {
    const displayCurrency = get(displayCurrencyAtom);

    return get(currenciesAtom).find((f) => f.code === displayCurrency) ?? get(siteCurrencyInfoAtom);
});
